<template>
  <div>
    <advisory v-if="advisory && advisory.body" :advisory="advisory" />
    <section class="content relative" v-show="viewing === 'request'">
      <div v-show="weightSettingsOpen" id="advanced-weight-settings" :class="[ weightSettingsOpen ? 'open' : '', 'clearfix' ]">
        <weight-volume-calculator
          ref="wgtVolCalc"
          @data-change="updateNetLbs"
          :ntg="quote.netToGross"
          :d="quote.density"
          :lbs="quote.wgtNetLbs"
          v-on:weight-close="weightSettingsOpen = false"
          :can-emit="isInitialised"
          :quote="quote"
        />
      </div>
      <div :class="[ weightSettingsOpen ? 'open' : '', 'form-steps-outer' ]">
        <form @submit.prevent="complete">
          <div class="backer clearfix">
            <div class="col-xs-12 col-md-4">
              <div class="form-group">
                <label class="form-label">{{ getConst(locale, 'FROM_LABEL') }}</label>
                <autocomplete
                  :dataset="locationSearchResults"
                  :title="getConst(locale, 'FROM_LABEL_TOOLTIP')"
                  :placeHolder="getConst(locale, 'FROM_INPUT_PLACEHOLDER')"
                  :initVal="initialLocations.fromLocation"
                  v-on:changing="data => locationSearch = data"
                  :fnFormatText="locationFormat"
                  :fnSelectValue="locationFormat"
                  v-on:selection-made="updateFrom"
                  :autoFocus="true"
                  :styles="{
                    ul: {
                      top: '42px'
                    },
                    input: {
                      border: '1px solid #c0c0c0 !important',
                      boxShadow: 'none'
                    }
                  }"
                >
                </autocomplete>
              </div>
            </div>
            <div class="col-xs-12 col-md-4">
              <div class="form-group">
                <label class="form-label">{{ getConst(locale, 'TO_LABEL') }}</label>
                <autocomplete
                  :dataset="locationSearchResults"
                  :title="getConst(locale, 'TO_LABEL_TOOLTIP')"
                  :placeHolder="getConst(locale, 'TO_INPUT_PLACEHOLDER')"
                  :initVal="initialLocations.toLocation"
                  v-on:changing="data => locationSearch = data"
                  :fnFormatText="locationFormat"
                  :fnSelectValue="locationFormat"
                  v-on:selection-made="updateTo"
                  :styles="{
                    ul: {
                      top: '42px'
                    },
                    input: {
                      border: '1px solid #c0c0c0 !important',
                      boxShadow: 'none'
                    }
                  }"
                />
              </div>
            </div>
            <div class="col-xs-12 col-md-3">
              <div class="form-group">
                <label for="weight" class="form-label">{{ getConst(locale, 'WEIGHT_LABEL') }}</label>
                <div class="input-group">
                  <input
                    style="border: 1px solid #c0c0c0 !important; boxShadow: none"
                    v-model="quote.wgtNetLbs"
                    data-toggle="tooltip"
                    :title="getConst(locale, 'WEIGHT_LABEL_TOOLTIP')"
                    :placeholder="getConst(locale, 'WEIGHT_INPUT_PLACEHOLDER')"
                    type="number"
                    class="form-control"
                    @change="recalculateWeightsAndVolume"
                    id="weight" />
                  <span data-toggle="tooltip" title="Click to enter in different units" class="input-group-addon" @click="weightSettingsOpen = !weightSettingsOpen">
                    <i class="fa fa-balance-scale" />
                  </span>
                </div>
              </div>
            </div>
            <div class="col-xs-12 col-md-1">
              <button type="submit" :disabled="!isValid || reloadingJobTypes" :class="[ !isValid || reloadingJobTypes ? 'disabled' : '', 'btn mt-25 sq primary p-10' ]">Go</button>
            </div>
            <div class="col-xs-12">
              <div class="badge-container">
                <quote-badge
                  v-for="badge in badges"
                  :key="badge.domId"
                  v-on:clicked="show(badge.domId)"
                  :badge="badge">
                </quote-badge>
              </div>
            </div>
          </div>
        </form>
      </div>
      <section id="advanced-settings-outer">
        <section class="adv-tabs">
          <div :class="[tab === 'ship' ? 'active' : '', 'tab' ]" @click="tab = 'ship'">
            Shipment
          </div>
          <div :class="[tab === 'customer' ? 'active' : '', 'tab' ]" @click="tab = 'customer'">
            Customer
          </div>
          <div :class="[ !isValid ? 'disabled' : '', tab === 'pref' ? 'active' : '', 'tab' ]" @click="updateTab('pref')">
            Preferences
          </div>
          <div v-if="rateFillerEnabled" :class="[ tab === 'filler' ? 'active' : '', 'tab' ]" @click="tab = 'filler'">
            Rate Filler
          </div>
        </section>
        <section class="adv-tabs-content">
          <div v-if="tab === 'ship'"  class="[tab === 'ship' ? 'active' : '', 'tab-content' ]">
            <div class="box-body">
              <div class="col-xs-12 col-md-6">
                <div class="form-group">
                  <label for="direction" class="form-label">{{ getConst(locale, 'DIRECTION_LABEL') }}</label>
                  <select class="form-control" id="direction" v-model="quote.direction">
                    <option v-for="option in getSystemOptions('DIRECTION')" :value="option" :selected="option.id === quote.direction.id">{{ option.name }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="transportType" class="form-label">{{ getConst(locale, 'TRANSPORT_TYPE_LABEL') }}</label>
                  <select class="form-control" id="transportType" v-model="quote.transportType">
                    <option v-for="option in getSystemOptions('TRANSPORT_TYPE')" :value="option" :selected="option.id === quote.transportType.id">{{ option.name }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="mode" class="form-label">{{ getConst(locale, 'MODE_LABEL') }}</label>
                  <select class="form-control" id="mode" v-model="quote.mode">
                    <option v-for="option in getSystemOptions('MODE')" :value="option" :selected="option.id === quote.mode.id">{{ option.name }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="service" class="form-label">{{ getConst(locale, 'SERVICE_LABEL') }}</label>
                  <select class="form-control" id="service" v-model="quote.service">
                    <option v-for="option in getSystemOptions('SERVICE_PROVIDED')" :value="option" :selected="option.id === quote.service.id">{{ option.name }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="category" class="form-label">{{ getConst(locale, 'CATEGORY_LABEL') }}</label>
                  <select class="form-control" id="category" v-model="quote.category">
                    <option v-for="option in getSystemOptions('SHIPMENT_CATEGORY')" :value="option" :selected="option.id === quote.category.id">{{ option.name }}</option>
                  </select>
                </div>
                <div v-if="isMultipleBrandsEnabled" class="form-group">
                  <label for="brand" class="form-label">{{ getConst(locale, 'SLICE_LABEL') }}</label>
                  <select class="form-control" id="brand" v-model="quote.brand">
                    <option value=""></option>
                    <option
                      v-for="option in userBrandGroups"
                      :value="option"
                      :selected="quote.brand && option.brands === quote.brand">
                      {{ option.name }}
                    </option>
                  </select>
                </div>
                <div class="form-group" v-show="showDistance">
                  <label for="distance" class="form-label">{{ getConst(locale, 'DISTANCE_LABEL') }}</label>
                  <input type="text" class="form-control" id="distance" v-model="quote.distance"/>
                </div>
                <div class="form-group" v-show="showDistance">
                  <label for="distanceType" class="form-label">{{ getConst(locale, 'DISTANCE_TYPE_LABEL') }}</label>
                  <select class="form-control" id="distanceType" v-model="quote.distanceType">
                    <option value=""></option>
                    <option
                      v-for="option in getSystemOptions('DISTANCE_TYPE')"
                      :value="option"
                      :selected="quote.distanceType && option.id === quote.distanceType.id">
                      {{ option.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-xs-12 col-md-6">
                <div class="form-group">
                  <label for="goodsType" class="form-label">{{ getConst(locale, 'GOODS_TYPE_LABEL') }}</label>
                  <select class="form-control" id="goodsType" v-model="quote.goodsType">
                    <option v-for="option in getSystemOptions('GOODS_TYPE')" :value="option" :selected="option.id === quote.goodsType.id">{{ option.name }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="containerType" class="form-label">{{ getConst(locale, 'CONTAINER_TYPE_LABEL') }}</label>
                  <select class="form-control" id="containerType" v-model="quote.containerType">
                    <option value=""></option>
                    <option v-for="option in getSystemOptions('CONTAINER_TYPE')" :value="option" :selected="option.id === quote.containerType.id">{{ option.name }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="woodLoose" class="form-label">{{ getConst(locale, 'WOOD_LOOSE_LABEL') }}</label>
                  <select class="form-control" id="woodLoose" v-model="quote.woodLoose">
                    <option value=""></option>
                    <option v-for="option in getSystemOptions('WOOD_LOOSE')" :value="option" :selected="quote.woodLoose && option.id === quote.woodLoose.id">{{ option.name }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="jobType" class="form-label">{{ getConst(locale, 'JOB_TYPE_LABEL') }}</label>
                  <select class="form-control" id="jobType" v-model="quote.jobType">
                    <option v-for="(jobtype, i) in availableJobTypes" :value="jobtype" :selected="quote.jobType.id === jobtype.id">{{ jobtype.name }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <label for="pickUpDate" class="form-label">Pick Up Date</label>
                  <input class="mai-date" type="date" v-model="quote.pickUpDate" @blur="validatePickUpDate"/>
                </div>
              </div>
            </div>
          </div>
          <div v-if="tab === 'customer'" :class="[tab === 'customer' ? 'active' : '', 'tab-content' ]">
            <div class="box-body">
              <div class="col-xs-12 col-md-6">
                <div class="form-group">
                  <label for="cust-fn" class="form-label">{{ getConst(locale, 'FIRSTNAME_LABEL') }}</label>
                  <input type="text" class="form-control" id="cust-fn" v-model="quote.firstName"/>
                </div>
                <div class="form-group">
                  <label for="cust-ln" class="form-label">{{ getConst(locale, 'LASTNAME_LABEL') }}</label>
                  <input type="text" class="form-control" id="cust-ln" v-model="quote.lastName"/>
                </div>
                <div class="form-group">
                  <label for="reference-number" class="form-label">{{ getConst(locale, 'EXTERNAL_REFERENCE') }}</label>
                  <div v-show="invalidReferenceNumber" class="validation-error">Invalid Reference Number</div>
                  <div v-if="isCrownRefFormatEnabled" class="flex-container">
                    <input type="text" class="form-control" v-model="referenceNumber.text1" @blur="validateReferenceText1" style="width: 60px" />
                    <span class="input-group-addon" style="padding-right: 15px;">-</span>
                    <input type="text" class="form-control" v-model="referenceNumber.text2" @blur="validateReferenceText2" style="width: 80px" />
                    <span class="input-group-addon" style="padding-right: 15px;">-</span>
                    <input type="text" class="form-control" v-model="referenceNumber.text3" @blur="validateReferenceText3" style="width: 50px" />
                  </div>
                  <input v-else type="text" class="form-control" id="reference-number" v-model="quote.reference"/>
                </div>
              </div>
              <div class="col-xs-12 col-md-6">
                <div class="form-group">
                  <label class="form-label">{{ getConst(locale, 'ACCOUNT_LABEL') }}</label>
                  <autocomplete
                    :dataset="partySearchResults"
                    :title="getConst(locale, 'ACCOUNT_TOOLTIP_LABEL')"
                    :placeHolder="getConst(locale, 'ACCOUNT_PLACEHOLDER')"
                    :groupAddons="addOnAccount({type: 'Account'})"
                    :initVal="initialParties.account"
                    v-on:changing="updatePartySearchAcc"
                    :fnFormatText="displayParty"
                    :fnSelectValue="displayParty"
                    v-on:selection-made="updateAccount"
                  />
                </div>
                <!--
                  <div class="form-group">
                    <label for="acc-contact" class="form-label">{{ getConst(locale, 'CONTACT_LABEL') }}</label>
                    <select class="form-control" id="acc-contact" v-model="quote.acc_contact">
                       <option v-for="contact in accountContacts" :value="contact">{{ contact }}</option>
                    </select>
                  </div>
                -->
                <div class="form-group">
                  <label class="form-label">{{ getConst(locale, 'BOOKER_LABEL') }}</label>
                  <autocomplete
                    :dataset="partySearchResults"
                    :title="getConst(locale, 'BOOKER_TOOLTIP_LABEL')"
                    :placeHolder="getConst(locale, 'BOOKER_PLACEHOLDER')"
                    :groupAddons="addOnBooker({type: 'Booker'})"
                    :initVal="initialParties.booker"
                    v-on:changing="updatePartySearchBook"
                    :fnFormatText="displayParty"
                    :fnSelectValue="displayParty"
                    v-on:selection-made="updateBooker"
                  />
                </div>
                <div class="form-group">
                  <label for="currency" class="form-label">{{ getConst(locale, 'CURRENCY_LABEL') }}</label>
                  <select class="form-control" id="currency" v-model="quote.currency">
                    <option v-for="(cur, i) in currencies" :value="cur" :selected="quote.currency && cur.id === quote.currency.id">{{ cur.name }}</option>
                  </select>
                </div>
                <!--
                  <div class="form-group">
                    <label for="book-contact" class="form-label">{{ getConst(locale, 'CONTACT_LABEL') }}</label>
                    <select class="form-control" id="book-contact" v-model="quote.book_contact">
                    </select>
                  </div>
                -->
              </div>
            </div>
          </div>
          <rate-filler
            v-if="tab === 'filler'"
            :routes="rateFillerAvailableRoutes"
            :services="rateFillerAvailableRates"
            @add-service="service => rateFillerAvailableRates.push(service)"
            :rate-request="rateRequest"
            @rate-filled="prefs => optimiseFromPrefs(prefs)"
          />
          <div :class="[tab === 'pref' ? 'active' : '', 'tab-content' ]">
            <div class="box-body">
              <prefs v-show="tab === 'pref' && isValid" :jobTypeId="quote.jobType.id" :displayParty="displayParty"></prefs>
              <h3 v-show="tab === 'pref' && !isValid">
                Ensure your chosen quote is valid before adding preferences
              </h3>
            </div>
          </div>
        </section>
      </section>
    </section>
    <quote-rates
      v-if="(viewing === 'response' && response !== null) && jobLoaded"
      :response="response"
      :request="reqDetails"
      :graphData="graphData"
      :fullRequest="rateRequest"
      :chargeProfile="chargeProfile"
      :job="job"
      :pref-with-notes="prefWithNotes"
      v-on:close-rates="viewing = 'request'"
      v-on:job-saved="goToJobPage"
      v-on:choice-made="updateOption"
      v-on:quoteDetailsUpdate="updateQuote"
    />
  </div>
</template>
<script>
  import quotes from '../mixins/quotes'
  import autocomplete from '../../helpers/AutoComplete.vue'
  import IEHackMixin from '../../helpers/IEHackMixin'
  import event from '../../../event'
  import API from '../../../api/endpoints'
  import WeightVolumeCalculator from './WeightVolumeCalculator.vue'
  import VueScrollTo from 'vue-scrollto'
  import Preferences from './Preferences'
  import QuoteRates from './QuoteRates.vue'
  import QuoteBadge from './QuoteBadge.vue'
  import DatePicker from 'vue-datepicker/vue-datepicker-es6.vue'
  import dateConfig from '../../../../static/js/date-picker-config.json'
  import QuoteFormRules from '../../../config/quote-form-rules'
  import debounce from 'lodash.debounce'
  import RateFiller from './RateFiller'
  import {addCryptRouteId, createRouteIdHash} from '../../helpers/RouteHash'
  import Advisory from '../../../components/Advisory'
  import { isPastDate } from '../../../util'
  export default {
    name: 'QuoteForm',
    mixins: [quotes, IEHackMixin, QuoteFormRules],
    components: {
      Advisory,
      prefs: Preferences,
      autocomplete: autocomplete,
      WeightVolumeCalculator,
      QuoteRates,
      QuoteBadge,
      DatePicker,
      RateFiller
    },
    data () {
      return {
        reloadingJobTypes: false,
        advisory: {},
        dateOption: dateConfig,
        tab: 'ship',
        graphData: null,
        response: null,
        viewing: 'request',
        jobLoaded: false,
        weightSettingsOpen: false,
        isInitialised: false,
        locationSearch: '',
        partySearchText: '',
        partyType: '',
        locationSearchResults: [],
        partySearchResults: [],
        initialLocations: {
          fromLocation: {},
          toLocation: {}
        },
        initialParties: {
          account: {},
          booker: {}
        },
        advancedOpen: false,
        prefsOpen: false,
        defaultsMap: {
          TARIFF_SET: 'brand',
          DIRECTION: 'direction',
          TRANSPORT_TYPE: 'transportType',
          MODE: 'mode',
          SERVICE_PROVIDED: 'service',
          SHIPMENT_CATEGORY: 'category',
          GOODS_TYPE: 'goodsType',
          CONTAINER_TYPE: 'containerType',
          WOOD_LOOSE: 'woodLoose',
          VOLUME_TYPE: 'volumeUnit',
          WEIGHT_TYPE: 'weightUnit'
        },
        quote: {
          id: 0,
          reference: '',
          fromLocation: {},
          toLocation: {},
          direction: '',
          transportType: '',
          mode: '',
          service: '',
          containerType: '',
          goodsType: '',
          brand: '',
          distance: '',
          distanceType: '',
          category: '',
          firstName: '',
          lastName: '',
          account: {},
          acc_contact: '',
          booker: {},
          book_contact: '',
          pickUpDate: null,
          currency: '',
          woodLoose: '',
          jobType: '',
          wgtNetLbs: 0,
          wgtNetKgs: 0,
          weightUnit: '',
          volNetCuf: 0,
          volNetM3: 0,
          netToGross: 0,
          volGrsCuf: 0,
          volGrsM3: 0,
          wgtGrsKgs: 0,
          wgtGrsLbs: 0,
          acwGrsKgs: 0,
          density: 0,
          volumeUnit: '',
          brandGroup: ''
        },
        toAddress: {
          address: '',
          street: '',
          location: '',
          county: '',
          state: '',
          postCode: '',
          country: ''
        },
        fromAddress: {
          address: '',
          street: '',
          location: '',
          county: '',
          state: '',
          postCode: '',
          country: ''
        },
        measureType: 'weight',
        availableJobTypes: [],
        rateFillerAvailableRates: [],
        rateFillerAvailableRoutes: [],
        rateFillerEnabled: false,
        job: null,
        rateFillers: []
      }
    },
    watch: {
      availableJobTypes (newVal) {
        if (newVal.length) {
          this.quote.jobType = newVal[0]
          if (this.quote.density !== newVal[0].density) {
            this.quote.density = newVal[0].density
            this.$nextTick(() => {
              this.$refs.wgtVolCalc.densityChange()
            })
          }
          if (this.quote.netToGross !== newVal[0].netToGross) {
            this.quote.netToGross = newVal[0].netToGross
            this.$nextTick(() => {
              this.$refs.wgtVolCalc.netToGrossChange()
            })
          }
        } else {
          this.quote.jobType = ''
        }
      },
      fromAddress: {
        deep: true,
        handler: function (newVal, oldVal) {
          if ((JSON.stringify(this.quote.fromLocation) !== JSON.stringify(newVal.location)) && this.isInitialised) {
            this.quote.fromLocation = newVal.location
          }
          if (JSON.stringify(this.initialLocations.fromLocation) !== JSON.stringify(newVal.location)) {
            this.initialLocations.fromLocation = newVal.location
          }
        }
      },
      toAddress: {
        deep: true,
        handler: function (newVal, oldVal) {
          if ((JSON.stringify(this.quote.toLocation) !== JSON.stringify(newVal.location)) && this.isInitialised) {
            this.quote.toLocation = newVal.location
          }
          if (JSON.stringify(this.initialLocations.toLocation) !== JSON.stringify(newVal.location)) {
            this.initialLocations.toLocation = newVal.location
          }
        }
      },
      'quote.account' (newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.initialParties.account)) {
          this.initialParties.account = newVal
        }
      },
      'quote.booker' (newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.initialParties.booker)) {
          this.initialParties.booker = newVal
        }
      },
      'quote.toLocation' (newVal, oldVal) {
        this.updateBasedOnToLocationChange(newVal)
        if (JSON.stringify(newVal) !== JSON.stringify(this.toAddress.location)) {
          this.toAddress.location = newVal
        }
      },
      'quote.fromLocation' (newVal, oldVal) {
        this.updateBasedOnFromLocationChange(newVal)
        if (JSON.stringify(newVal) !== JSON.stringify(this.fromAddress.location)) {
          this.fromAddress.location = newVal
        }
      },
      'quote.transportType' (transportType) {
        if (transportType.code === 'A') {
          this.quote.mode = this.getSystemOptionByCodeAndType('AIR', 'MODE') || {}
          this.quote.containerType = this.getSystemOptionByCodeAndType('AIR_VAN', 'CONTAINER_TYPE') || {}
        } else if (transportType.code === 'R') {
          this.quote.mode = this.getSystemOptionByCodeAndType('RORO', 'MODE') || {}
        } else if (transportType.code === 'S') {
          this.updateBasedOnVolNetCbm(false)
        } else if (transportType.code === 'ST') {
          this.quote.mode = this.getSystemOptionByCodeAndType('STORAGE', 'MODE') || {}
          this.quote.containerType = {}
        }
        this.quote.woodLoose = {}
        this.updateAvailableJobTypes()
      },
      'quote.woodLoose' (newVal, oldVal) {
        this.updateAvailableJobTypes()
      },
      'quote.service' (newVal, oldVal) {
        this.updateAvailableJobTypes()
      },
      'quote.mode' (newVal, oldVal) {
        this.quote.woodLoose = {}
        this.updateAvailableJobTypes()
      },
      'quote.direction' (direction) {
        if (direction && direction.code === 'S') {
          this.quote.transportType = this.getSystemOptionByCodeAndType('ST', 'TRANSPORT_TYPE')
        }
        this.updateAvailableJobTypes()
      },
      'quote.category' (newVal, oldVal) {
        this.updateAvailableJobTypes()
      },
      'quote.goodsType' (newVal, oldVal) {
        this.updateAvailableJobTypes()
      },
      '$route' (newVal, oldVal) {
        this.isInitialised = false
        this.init()
      },
      locationSearch (newVal, oldVal) {
        if (newVal.trim().length > 1) {
          this.updateLocationSearchResults(newVal)
        }
      },
      partySearchText (newVal, oldVal) {
        if (newVal.trim().length > 1) {
          this.updatePartySearchResults(newVal)
        }
      },
      metrics (newVal, oldVal) {
        if (((oldVal || {}).cubicM || {}).net !== ((newVal || {}).cubicM || {}).net) {
          this.updateBasedOnVolNetCbm(true)
        }
      }
    },
    computed: {
      brandGroups () {
        return this.$store.getters['systemOption/brandGroups']
      },
      chargeProfile () {
        return this.quote.account && this.quote.account.chargeProfile && this.quote.account.chargeProfile.id
      },
      badges () {
        let badges = []
        let ignores = ['VOLUME_TYPE', 'WEIGHT_TYPE']
        for (let prop in this.defaultsMap) {
          if (this.quote[this.defaultsMap[prop]] !== null && ignores.indexOf(prop) === -1) {
            badges.push({
              key: this.deCamel(this.defaultsMap[prop]),
              val: this.quote[this.defaultsMap[prop]] && this.quote[this.defaultsMap[prop]].name,
              domId: this.defaultsMap[prop]
            })
          }
        }
        if (this.quote.jobType !== null) {
          badges.push({ key: 'Job Type', val: this.quote.jobType.name, domId: 'jobType' })
        }
        return badges
      },
      addOnAccount (data) {
        return (data) => {
          return [
            {
              icon: 'fa-search',
              fn: this.partySearch(data, 'account-search', 'Party Search - Account')
            }
          ]
        }
      },
      addOnBooker (data) {
        return (data) => {
          return [
            {
              icon: 'fa-search',
              fn: this.partySearch(data, 'booker-search', 'Party Search - Booker')
            }
          ]
        }
      },
      addOnFrom (address) {
        return (address) => {
          return [
            {
              icon: 'fa-search',
              fn: this.search(address, 'from-address', 'Location lookup - from address'),
              styles: {
                i: {
                  color: '#000'
                },
                span: {
                  backgroundColor: '#fff'
                }
              }
            }
          ]
        }
      },
      addOnTo (address) {
        return (address) => {
          return [
            {
              icon: 'fa-search',
              fn: this.search(address, 'to-address', 'Location lookup - to address'),
              styles: {
                i: {
                  color: '#000'
                },
                span: {
                  backgroundColor: '#fff'
                }
              }
            }
          ]
        }
      },
      userBrandGroups () {
        const brands = this.brandGroups
        if (this.isMultipleBrandsEnabled) {
          const userBrands = this.user.brands ? this.user.brands.split(',') : []
          return brands.filter(brand => userBrands.includes(brand.code))
        }
        return brands
      },
      countries () {
        return this.$store.getters['systemOption/countries']
      },
      currencies () {
        return this.$store.getters['systemOption/currencies']
      },
      fromCountry () {
        return !this.quote.fromLocation.country ? null : this.quote.fromLocation.country
      },
      isFromHomeCountry () {
        return this.fromCountry && this.fromCountry.id ? this.fromCountry.id === this.userDefaultCountry.id : false
      },
      isMultipleBrandsEnabled () {
        return this.isSystemOptionParameterEnabled('ENABLE_MULTIPLE_USER_BRANDS')
      },
      isToHomeCountry () {
        return this.toCountry && this.toCountry.id ? this.toCountry.id === this.userDefaultCountry.id : false
      },
      isValid () {
        return this.jobTypeValid && this.quote.wgtNetLbs > 0 && this.toValid && this.fromValid && (!this.isMultipleBrandsEnabled || this.quote.brand) && this.isPickUpDateValid
      },
      fromValid () {
        return this.quote.fromLocation.name
      },
      toValid () {
        return this.quote.toLocation.name
      },
      jobTypeValid () {
        return this.quote.jobType.id
      },
      jobTypes () {
        return this.$store.getters['systemOption/jobTypes']
      },
      locations () {
        return this.$store.getters['quote/locations']
      },
      metrics () {
        return this.$store.getters['quote/metrics']
      },
      partyGroups () {
        return this.$store.getters['authentication/userPartyGroups']
      },
      prefs () {
        return this.$store.getters['quote/prefs']
      },
      prefsFormattedForMaiReq () {
        return this.prefs.map(p => {
          return {
            Service: p.id,
            ServiceProvider: p.provider.id,
            OriginLocation: p.fromLocation.id,
            DestinationLocation: p.toLocation.id,
            Amount: p.Amount,
            Currency: p.Currency
          }
        })
      },
      prefsFormatted () {
        return this.prefs.map(p => {
          const parsed = parseFloat(p.Amount)
          const amount = parsed === 0 ? 0 : (parsed || null)
          return {
            Service: p.code || null,
            ServiceProvider: p.provider.code || '',
            OriginTown: p.fromLocation.name || null,
            OriginState: p.fromLocation.subdivision || null,
            OriginCountry: p.fromLocation.country ? p.fromLocation.country.code : null,
            DestinationTown: p.toLocation.name || null,
            DestinationState: p.toLocation.subdivision || null,
            DestinationCountry: p.toLocation.country ? p.toLocation.country.code : null,
            Amount: amount,
            Currency: p.Currency.code || null
          }
        }).filter(p => {
          let hasEntries = false
          for (let prop in p) {
            if (prop !== 'Service' && p[prop]) {
              hasEntries = true
            }
          }
          return hasEntries
        })
      },
      quotes () {
        return this.$store.getters['quote/quotes']
      },
      reqDetails () {
        return {
          origin: this.quote.fromLocation.name,
          dest: this.quote.toLocation.name,
          firstName: this.quote.firstName,
          lastName: this.quote.lastName,
          pickUpDate: this.quote.pickUpDate,
          booker: this.quote.booker.name,
          account: this.quote.account.name,
          jobType: this.quote.jobType.name,
          brandGroup: this.quote.brand ? this.quote.brand.code : null,
          weightsAndVolume: this.metrics
        }
      },
      userBrandCode () {
        return this.$store.getters['authentication/userBrand'] && this.$store.getters['authentication/userBrand'].code
      },
      rateRequest () {
        return {
          'Reference': this.quote.reference,
          'Originator': 'mai-opti',
          'User': this.user.id,
          'Brand': (this.quote.brand && this.quote.brand.brands) || this.userBrandCode,
          'Date': '',
          'OriginTown': this.quote.fromLocation && this.quote.fromLocation.name,
          'OriginState': this.quote.fromLocation && this.quote.fromLocation.subdivision,
          'OriginCountry': (this.quote.fromLocation && this.quote.fromLocation.country && this.quote.fromLocation.country.code) || '',
          'DestinationTown': this.quote.toLocation && this.quote.toLocation.name,
          'DestinationState': this.quote.toLocation && this.quote.toLocation.subdivision,
          'DestinationCountry': (this.quote.toLocation && this.quote.toLocation.country && this.quote.toLocation.country.code) || '',
          'JobType': this.quote.jobType && this.quote.jobType.code,
          'GoodsType': this.quote.goodsType && this.quote.goodsType.code,
          'ContainerType': this.quote.containerType && this.quote.containerType.code,
          WeightNetLbs: parseFloat(this.metrics.weightLbs.net),
          WeightNetKgs: parseFloat(this.metrics.weightKgs.net),
          WeightGrsLbs: parseFloat(this.metrics.weightLbs.gross),
          WeightGrsKgs: parseFloat(this.metrics.weightKgs.gross),
          VolumeNetCuf: parseFloat(this.metrics.cubicFt.net),
          VolumeNetCbm: parseFloat(this.metrics.cubicM.net),
          VolumeGrsCuf: parseFloat(this.metrics.cubicFt.gross),
          VolumeGrsCbm: parseFloat(this.metrics.cubicM.gross),
          ACW: parseFloat(this.metrics.ACW),
          Density: this.metrics.density,
          'WeightType': 'LBS',
          'Distance': this.quote.distance,
          'DistanceType': this.quote.distanceType && this.quote.distanceType.code,
          'Account': this.quote.account && this.quote.account.code,
          'Booker': this.quote.booker && this.quote.booker.code,
          'PickupDate': `${this.quote.pickUpDate}T00:00:00.000Z`,
          'ResponseCurrency': this.quote.currency && this.quote.currency.code,
          'MaxNumberOfOptions': 16,
          'Preferences': this.prefsFormatted,
          WoodLoose: this.quote.woodLoose && this.quote.woodLoose.code
        }
      },
      maiRateRequest () {
        return {
          Reference: this.quote.reference,
          Originator: 'mai-opti',
          User: this.user.id,
          Brand: (this.quote.brand && this.quote.brand.code) || this.userBrandCode,
          Date: null,
          OriginLocation: this.quote.fromLocation.id,
          DestinationLocation: this.quote.toLocation.id,
          JobType: this.quote.jobType.id,
          GoodsType: this.quote.goodsType.id,
          ContainerType: this.quote.containerType.id,
          WeightNetLbs: parseFloat(this.metrics.weightLbs.net),
          WeightNetKgs: parseFloat(this.metrics.weightKgs.net),
          WeightGrsLbs: parseFloat(this.metrics.weightLbs.gross),
          WeightGrsKgs: parseFloat(this.metrics.weightKgs.gross),
          VolumeNetCuf: parseFloat(this.metrics.cubicFt.net),
          VolumeNetCbm: parseFloat(this.metrics.cubicM.net),
          VolumeGrsCuf: parseFloat(this.metrics.cubicFt.gross),
          VolumeGrsCbm: parseFloat(this.metrics.cubicM.gross),
          ACW: parseFloat(this.metrics.ACW),
          Density: this.metrics.density,
          Account: this.quote.account.id,
          Booker: this.quote.booker.id,
          PickupDate: null,
          Distance: this.quote.distance,
          DistanceType: this.quote.distanceType && this.quote.distanceType.id,
          ResponseCurrency: this.quote.currency.id,
          MaxNumberOfOptions: 16,
          Preferences: this.prefsFormattedForMaiReq
        }
      },
      showDistance () {
        return this.quote.mode && this.quote.mode.code === 'EURO_ROAD'
      },
      systemOptions () {
        return this.$store.getters['systemOption/options']
      },
      toCountry () {
        return !this.quote.toLocation.country ? {} : this.quote.toLocation.country
      },
      user () {
        return this.$store.getters['authentication/user']
      },
      userDefaultCurrency () {
        return this.$store.getters['authentication/defaultCurrency']
      },
      userDefaultCountry () {
        return this.$store.getters['authentication/defaultCountry'] || {}
      },
      jobTypeParams () {
        return {
          nature: this.quote.direction.id,
          mode: this.quote.mode.id,
          transportType: this.quote.transportType.id,
          serviceProvided: this.quote.service.id,
          shipmentType: this.quote.category.id,
          woodLoose: this.quote.woodLoose && this.quote.woodLoose.id,
          goodsType: this.quote.goodsType.id
        }
      },
      isPickUpDateValid () {
        return !this.quote.pickUpDate || !isPastDate(this.quote.pickUpDate)
      },
      prefWithNotes () {
        const result = []
        this.addPrefWithNotes(this.prefs, result)
        this.addPrefWithNotes(this.rateFillers, result)
        return result
      }
    },
    methods: {
      addPrefWithNotes (prefs, result) {
        if (prefs && prefs.length > 0) {
          prefs.filter(pref => pref.note).forEach(pref => {
            result.push({serviceCode: pref.service || pref.code, note: pref.note})
          })
        }
      },
      clearQuoteDistance () {
        this.quote.distance = null
        this.quote.distanceType = null
      },
      displayParty (p) {
        return p && p.name && p.code ? `${p.name}, ${p.code}` : ''
      },
      locationFormat (location) {
        let str = ''
        if (location.name) {
          str += location.name
        }
        if (location.subdivision) {
          str += `, ${location.subdivision}`
        }
        if (typeof location.country === 'object' && location.country !== null && location.country.hasOwnProperty('code')) {
          str += `, ${location.country.code}`
        }
        return str
      },
      show (domId) {
        if (domId) {
          this.tab = 'ship'
          const el = document.getElementById(domId)
          if (el) {
            switch (el.nodeName.toLowerCase()) {
              case 'select':
                el.focus()
                // this.forceOpen(el)
                break
            }
            VueScrollTo.scrollTo(`#${domId}`, 500, {})
          }
        }
      },
      forceOpen (element) {
        if (document.createEvent) {
          let e = document.createEvent('MouseEvents')
          e.initMouseEvent('mousedown', false, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null)
          element.dispatchEvent(e)
        } else if (element.fireEvent) {
          element.fireEvent('onmousedown')
        }
      },
      deCamel (string) {
        return string.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
      },
      updateTab (str) {
        if (this.isValid) {
          this.tab = str
        }
      },
      updateOption (data) {
        if (!data.hasOwnProperty('updateEntireRoute')) {
          this.updateSingleItem(data)
        } else {
          this.updateEntireRoute(data)
        }
      },
      updateSingleItem ({ optionNumber, itemIndex, option }) {
        let opt = this.response.option.find(opt => opt.optionNumber === optionNumber)
        if (opt) {
          for (let prop in option) {
            if (opt.item[itemIndex].hasOwnProperty(prop)) {
              opt.item[itemIndex][prop] = option[prop]
            }
          }
          opt.optionTotal = opt.item.reduce((total, {baseRate}) => {
            return total + baseRate
          }, 0).toFixed(2)
        }
      },
      updateEntireRoute ({ optionNumber, option }) {
        option.item.forEach((item, itemIndex) => {
          this.updateSingleItem({ optionNumber, itemIndex, option: item })
        })
      },
      autoCompleteFilter (searchString, item) {
        return JSON.stringify(item.initValue.toLowerCase()).indexOf(searchString.toLowerCase()) !== -1
      },
      complete () {
        if (this.isValid) {
          if (this.isCrownRefFormatEnabled && !this.invalidReferenceNumber) {
            this.quote.reference = this.crownReferenceNumber
          }
          this.sendRateRequest()
        } else {
          event.$emit('toast', { message: 'Please complete all required fields', type: 'warning' })
        }
      },
      getCountry (cId) {
        return this.countries.find(({id}) => id === cId) || null
      },
      getDefault (optionTypeName) {
        let option = null
        this.systemOptions.every(({optionType, code, valueAlpha}) => {
          if (optionType !== null && optionType.hasOwnProperty('code') && optionType.code === 'DEFAULT' && code === optionTypeName) {
            option = valueAlpha
            return false
          }
          return true
        })
        if (optionTypeName === 'TARIFF_SET') {
          option = this.userBrandGroups.find(({code}) => code === option)
        } else {
          this.systemOptions.every(o => {
            if (o.hasOwnProperty('optionType') && o.optionType !== null && o.optionType.hasOwnProperty('code') && o.optionType.code === optionTypeName && o.code === option) {
              option = o
              return false
            }
            return true
          })
        }
        return option
      },
      getDataOptionIdFromCode (findOptionType, findCode) {
        let option = this.systemOptions.find(({optionType, code}) => optionType === findOptionType && findCode === code)
        return option ? option.id : null
      },
      getId (type, val) {
        return true
      },
      getSystemOption (doId) {
        return this.systemOptions.find(({id}) => id === doId) || {}
      },
      getSystemOptionByCodeAndType (_code, optionTypeCode) {
        return this.systemOptions.find(({code, optionType}) => code === _code && optionType && optionType.code === optionTypeCode) || {}
      },
      getSystemOptions (code) {
        return this.systemOptions.filter(({optionType}) => {
          return optionType !== null && optionType.hasOwnProperty('code') && optionType.code === code
        }).sort((a, b) => {
          return a.name < b.name ? -1 : a.name > b.name ? 1 : 0
        })
      },
      isSystemOptionParameterEnabled (whichCode) {
        return this.$store.getters['systemOption/isSystemParameterEnabled'](whichCode)
      },
      loadJob (id) {
        return new Promise((resolve, reject) => {
          this.$http.get(`${API.JOB}/${id}`)
            .then(({data}) => resolve(data))
            .catch(reject)
        })
      },
      setJobDataFromUrl () {
        const jobId = addCryptRouteId(this.$route.query.job)
        this.loadJob(jobId)
          .then(data => {
            const job = data.job
            this.quote.firstName = job.firstName
            this.quote.lastName = job.lastName
            this.quote.pickUpDate = job.pickupDate ? (job.pickupDate.includes('T') ? job.pickupDate.substring(0, job.pickupDate.indexOf('T')) : null) : null
            this.quote.brand = job.brandGroup
            if (this.isCrownRefFormatEnabled && job.externalRef) {
              this.updateReferenceNumbers(job.externalRef)
            } else {
              this.quote.reference = job.externalRef
            }
            if (job.account) {
              this.quote.account = job.account
            }
            if (job.booker) {
              this.quote.booker = job.booker
            }
            if (job.quoteCurrency) {
              this.quote.currency = job.quoteCurrency
            }
            this.job = data
          })
          .catch(console.err)
          .finally(() => { this.jobLoaded = true })
      },
      setLocationsFromUrl () {
        const query = this.$route.query
        const fromLocation = {
          name: query.fromN || null,
          subdivision: query.fromS || null,
          country: query.fromC && { code: query.fromC }
        }
        const toLocation = {
          name: query.toN || null,
          subdivision: query.toS || null,
          country: query.toC && { code: query.toC }
        }
        if (query.fromN) {
          this.quote.fromLocation = fromLocation
        }
        if (query.toN) {
          this.quote.toLocation = toLocation
        }
      },
      setWeightsFromUrl () {
        const query = this.$route.query
        if (query.wgtNetLbs) {
          this.quote.wgtNetLbs = query.wgtNetLbs
        }
        if (query.wgtNetKgs) {
          this.quote.wgtNetKgs = query.wgtNetKgs
        }
        if (query.volNetCuf) {
          this.quote.volNetCuf = query.volNetCuf
        }
        if (query.volNetM3) {
          this.quote.volNetM3 = query.volNetM3
        }
        if (query.density) {
          this.quote.density = query.density
        }
        if (query.netToGross) {
          this.quote.netToGross = query.netToGross
        }
        if (query.volGrsCuf) {
          this.quote.volGrsCuf = query.volGrsCuf
        }
        if (query.volGrsM3) {
          this.quote.volGrsM3 = query.volGrsM3
        }
        if (query.wgtGrsKgs) {
          this.quote.wgtGrsKgs = query.wgtGrsKgs
        }
        if (query.wgtGrsLbs) {
          this.quote.wgtGrsLbs = query.wgtGrsLbs
        }
        if (query.acwGrsKgs) {
          this.quote.acwGrsKgs = query.acwGrsKgs
          this.$refs.wgtVolCalc.setValue('ACW', query.acwGrsKgs)
        }
      },
      setDataOptionsFromUrl () {
        const query = this.$route.query
        if (query.containerType) {
          this.quote.containerType = this.getSystemOption(query.containerType) || {}
        }
        if (query.jobType) {
          this.$http.get(API.GET_ADMIN_ENTITY_BY_ID('job-type', query.jobType))
            .then(({data}) => {
              if (data) {
                const jobTypePropToQuoteProp = {
                  goodsType: 'goodsType',
                  mode: 'mode',
                  nature: 'direction',
                  serviceProvided: 'service',
                  shipmentType: 'category',
                  transportType: 'transportType',
                  woodLoose: 'woodLoose'
                }
                for (let prop in jobTypePropToQuoteProp) {
                  const quoteProp = jobTypePropToQuoteProp[prop]
                  this.quote[quoteProp] = data[prop] ? (this.getSystemOption(data[prop]) || {}) : {}
                }
                this.availableJobTypes = [data]
                this.quote.jobType = data
              }
            }).catch(console.error)
        }
      },
      setDefaultCurrency () {
        this.quote.currency = this.userDefaultCurrency
      },
      init () {
        this.initAdvisory()
        const autofillFromParams = this.$route.query && this.$route.query.autofillFromParams
        if (autofillFromParams) {
          this.setJobDataFromUrl()
          this.setLocationsFromUrl()
          this.setWeightsFromUrl()
          this.setDataOptionsFromUrl()
        } else {
          const initialData = this.initialData()
          for (let prop in initialData) {
            this[prop] = initialData[prop]
          }
          this.setDefaults()
          this.jobLoaded = true
        }
        this.setDefaultCurrency()
        this.initialLocations.fromLocation = this.quote.fromLocation
        this.initialLocations.toLocation = this.quote.toLocation
        this.initialParties.account = this.quote.account
        this.initialParties.booker = this.quote.booker
        this.isInitialised = true
      },
      initAdvisory () {
        const fromLogin = this.$route.query && this.$route.query.fromLogin
        if (fromLogin === 'true') {
          const userSettingsAdvisory = this.$store.getters['authentication/userSettingsAdvisory']
          if (!userSettingsAdvisory || userSettingsAdvisory !== 'HIDE') {
            this.$store.dispatch('library/fetchAdvisory')
              .then(({data}) => {
                const advisory = data ? data[0] : null
                if (advisory) {
                  this.advisory = { title: advisory.name, body: advisory.detail }
                }
              }).catch(console.log)
          }
          this.$store.dispatch('library/fetchQuoteExternalLink')
        }
      },
      initialData () {
        return {
          advancedOpen: false,
          quote: {
            id: 0,
            fromLocation: '',
            toLocation: '',
            wgtNetLbs: '',
            wgtNetKgs: '',
            weightUnit: '',
            volNetCuf: '',
            volNetM3: '',
            netToGross: '',
            volGrsCuf: '',
            volGrsM3: '',
            wgtGrsKgs: '',
            wgtGrsLbs: '',
            acwGrsKgs: '',
            volumeUnit: '',
            density: '',
            direction: '',
            woodLoose: '',
            transportType: '',
            mode: '',
            service: '',
            containerType: '',
            goodsType: '',
            brand: '',
            distance: '',
            distanceType: '',
            category: '',
            firstName: '',
            lastName: '',
            account: {},
            acc_contact: '',
            pickUpDate: null,
            booker: {},
            book_contact: '',
            jobType: '',
            currency: '',
            brandGroup: ''
          },
          measureType: 'weight'
        }
      },
      respondToFromAddressChange (data) {
        if (this.shouldRespond(data)) {
          for (let prop in data.data) {
            if (this.fromAddress.hasOwnProperty(prop)) {
              this.fromAddress[prop] = data.data[prop]
            }
          }
        }
      },
      respondToToAddressChange (data) {
        if (this.shouldRespond(data)) {
          for (let prop in data.data) {
            if (this.toAddress.hasOwnProperty(prop)) {
              this.toAddress[prop] = data.data[prop]
            }
          }
        }
      },
      respondToAccountChange (data) {
        if (this.shouldRespond(data)) {
          this.quote.account = data.data.choice
        }
      },
      respondToBookerChange (data) {
        if (this.shouldRespond(data)) {
          this.quote.booker = data.data.choice
        }
      },
      partySearch (input, eventName, title) {
        const uid = this._uid
        var data = input
        return () => {
          event.$emit('right-open', {
            callerId: uid,
            component: 'PartyLookup',
            data: data,
            event: eventName,
            title: title
          })
        }
      },
      search (input, eventName, title) {
        const uid = this._uid
        var data = input
        return () => {
          event.$emit('right-open', {
            callerId: uid,
            component: 'location',
            data: data,
            event: eventName,
            title: title
          })
        }
      },
      sendMaiRequest () {
        event.$emit('loading-on', {})
        this.$store.dispatch('quote/sendMaiRequest', this.maiRateRequest)
          .then(res => {
            this.viewing = 'response'
            this.response = res
          }).catch(err => {
            event.$emit('toast', { message: err, type: 'error' })
          }).then(() => {
            event.$emit('loading-off', {})
          })
      },
      optimiseFromPrefs (prefs) {
        const rateRequest = this.rateRequest
        rateRequest.Preferences = prefs.map(p => {
          return {
            Service: p.service,
            ServiceProvider: p.serviceProviderCode,
            OriginTown: p.originTown,
            OriginState: p.originState,
            OriginCountry: p.originCountry,
            DestinationTown: p.destinationTown,
            DestinationState: p.destinationState,
            DestinationCountry: p.destinationCountry,
            Amount: (p.amount && parseFloat(p.amount)) || null,
            Currency: p.currency
          }
        })
        this.rateFillers = prefs
        this.handleRateRequest(rateRequest)
      },
      handleRateRequest (requestBody) {
        event.$emit('loading-on', {})
        this.tab = 'ship'
        this.$store.dispatch('quote/sendRateRequest', requestBody)
          .then(res => {
            this.viewing = 'response'
            this.response = res
          }).catch(err => {
            const data = JSON.parse(err)
            if (data && data.availableRates && data.possibleRoutes) {
              this.rateFillerAvailableRates = data.availableRates
              this.rateFillerAvailableRoutes = data.possibleRoutes
              this.rateFillers = []
              this.rateFillerEnabled = true
              this.tab = 'filler'
            } else {
              event.$emit('toast', { message: JSON.parse(err).responseNotes, type: 'error' })
            }
          }).then(() => {
            event.$emit('loading-off', {})
          })
        this.$store.dispatch('quote/sendGraphRequest', requestBody)
          .then(res => {
            this.graphData = res
          }).catch(err => {
            console.log(err)
            event.$emit('toast', { message: 'Error loading graph data - network visuals may be unavailable', type: 'error' })
          })
      },
      recalculateWeightsAndVolume () {
        this.$refs.wgtVolCalc.weightLbsNetChange()
      },
      sendRateRequest () {
        this.handleRateRequest(this.rateRequest)
      },
      setDefaults () {
        for (let optionTypeName in this.defaultsMap) {
          this.quote[this.defaultsMap[optionTypeName]] = this.getDefault(optionTypeName)
        }
      },
      shouldRespond (data) {
        return data.callerId === this._uid
      },
      optDirectionId () {
        return this.getDataOptionIdFromCode('DIRECTION', this.quote.direction)
      },
      optModeId () {
        return this.getDataOptionIdFromCode('MODE', this.quote.mode)
      },
      optTransportId () {
        return this.getDataOptionIdFromCode('TRANSPORT_TYPE', this.quote.transportType)
      },
      optServiceId () {
        return this.getDataOptionIdFromCode('SERVICE_PROVIDED', this.quote.service)
      },
      optShipId () {
        return this.getDataOptionIdFromCode('SHIPMENT_CATEGORY', this.quote.category)
      },
      optWoodLooseId () {
        return this.getDataOptionIdFromCode('WOOD_LOOSE', this.quote.woodLoose)
      },
      updateJobTypes () {
        if (this.jobTypeParams && this.jobTypeParams.serviceProvided) {
          this.$store.dispatch('quote/getAvailableJobTypes', this.jobTypeParams).then(({data}) => {
            this.availableJobTypes = data
          }).catch(err => {
            console.log(err)
            this.availableJobTypes = []
          }).finally(() => {
            this.reloadingJobTypes = false
          })
        }
      },
      getJobTypes: debounce(function () { this.updateJobTypes() }, 500),
      updateAvailableJobTypes () {
        this.reloadingJobTypes = true
        this.getJobTypes(Object.assign({}, this.jobTypeParams))
      },
      isMatch (a, b) {
        return (a == b) || (a === null && !b)
      },
      updateLocationSearchResults (str) {
        this.$http.get(API.GET_LOCATIONS_NAME_LIKE(str))
          .then(({data}) => {
            if (str === this.locationSearch) {
              this.$store.dispatch('quote/mapLocationsWithCountry', data)
              .then(res => {
                this.locationSearchResults = res
              })
              .catch(err => {
                console.log(err)
                this.locationSearchResults = data
              })
            }
          })
          .catch((err) => {
            console.log(err)
          })
      },
      updatePartySearchAcc (data) {
        this.partySearchText = data
        this.partyType = 'CORP'
      },
      updatePartySearchBook (data) {
        this.partySearchText = data
        this.partyType = 'SP'
      },
      updatePartySearchResults (str) {
        let params = {
          name: str,
          category: this.partyType,
          partyGroups: JSON.stringify(this.partyGroups.map(({id}) => id))
        }
        if (this.user.brand) {
          params.brand = this.user.brand
        }
        this.$http.get(API.GET_PARTIES, { params })
          .then(({data}) => {
            if (str === this.partySearchText) {
              this.partySearchResults = data
            }
          })
          .catch((err) => {
            console.log(err)
          })
      },
      updateBasedOnVolNetCbm (shouldUpdateTransportType) {
        const val = (this.metrics && this.metrics.cubicM && this.metrics.cubicM.net) || 0
        const values = this.getBasedOnVolNetCbmRules(val, shouldUpdateTransportType)
        for (let prop in values) {
          if (this.quote.hasOwnProperty(prop)) {
            this.quote[prop] = values[prop]
          }
        }
      },
      updateBasedOnFromLocationChange (location) {
        if (this.isFromHomeCountry && this.isToHomeCountry) {
          this.quote.direction = this.quote.direction || this.getSystemOptionByCodeAndType('Domestic', 'DIRECTION')
          this.quote.transportType = this.getSystemOptionByCodeAndType('R', 'TRANSPORT_TYPE')
        } else if (this.isFromHomeCountry) {
          this.quote.direction = this.quote.direction || this.getSystemOptionByCodeAndType('E', 'DIRECTION')
          this.quote.transportType = this.getSystemOptionByCodeAndType('S', 'TRANSPORT_TYPE')
        } else if (this.isToHomeCountry) {
          this.quote.direction = this.quote.direction || this.getSystemOptionByCodeAndType('I', 'DIRECTION')
          this.quote.transportType = this.getSystemOptionByCodeAndType('S', 'TRANSPORT_TYPE')
        } else {
          this.quote.direction = this.quote.direction || this.getSystemOptionByCodeAndType('TC', 'DIRECTION')
          this.quote.transportType = this.getSystemOptionByCodeAndType('S', 'TRANSPORT_TYPE')
        }
        this.clearQuoteDistance()
      },
      updateBasedOnToLocationChange (location) {
        if (this.isFromHomeCountry && this.isToHomeCountry) {
          this.quote.direction = this.quote.direction || this.getSystemOptionByCodeAndType('Domestic', 'DIRECTION')
          this.quote.transportType = this.getSystemOptionByCodeAndType('R', 'TRANSPORT_TYPE')
        } else if (this.isFromHomeCountry) {
          this.quote.direction = this.quote.direction || this.getSystemOptionByCodeAndType('E', 'DIRECTION')
          this.quote.transportType = this.getSystemOptionByCodeAndType('S', 'TRANSPORT_TYPE')
        } else if (this.isToHomeCountry) {
          this.quote.direction = this.quote.direction || this.getSystemOptionByCodeAndType('I', 'DIRECTION')
          this.quote.transportType = this.getSystemOptionByCodeAndType('S', 'TRANSPORT_TYPE')
        } else {
          this.quote.direction = this.quote.direction || this.getSystemOptionByCodeAndType('TC', 'DIRECTION')
          this.quote.transportType = this.getSystemOptionByCodeAndType('S', 'TRANSPORT_TYPE')
        }
        this.clearQuoteDistance()
      },
      updateNetLbs (wgtNetLbs) {
        if (this.isInitialised) {
          this.quote.wgtNetLbs = wgtNetLbs
        }
      },
      updateTo (to) {
        this.quote.toLocation = to
        this.locationSearch = ''
        this.locationSearchResults = []
      },
      updateFrom (from) {
        this.quote.fromLocation = from
        this.locationSearch = ''
        this.locationSearchResults = []
      },
      updateAccount (acc) {
        this.quote.account = acc
        this.partySearchText = ''
        this.partySearchResults = []
      },
      updateBooker (b) {
        this.quote.booker = b
        this.partySearchText = ''
        this.partySearchResults = []
      },
      updateQuote (data) {
        for (let prop in data) {
          if (this.quote.hasOwnProperty(prop)) {
            this.quote[prop] = data[prop]
          }
        }
      },
      validatePickUpDate () {
        if (!this.isPickUpDateValid) {
          this.quote.pickUpDate = null
          event.$emit('toast', { message: 'Please do not enter past date', type: 'warning' })
        }
      },
      enter (e) {
        /*
        if (e.keyCode === 13 && this.viewing === 'request') {
          this.complete()
        }
        */
      },
      goToJobPage (job) {
        this.$router.push({ name: 'Admin - Edit/View Job', params: { id: createRouteIdHash(job.id) } })
      }
    },
    mounted () {
      this.init()
      event.$on('from-address', this.respondToFromAddressChange)
      event.$on('to-address', this.respondToToAddressChange)
      event.$on('account-search', this.respondToAccountChange)
      event.$on('booker-search', this.respondToBookerChange)
      document.addEventListener('keydown', this.enter, { passive: true })
    },
    beforeDestroy () {
      event.$off('from-address', this.respondToFromAddressChange)
      event.$off('to-address', this.respondToToAddressChange)
      event.$off('account-search', this.respondToAccountChange)
      event.$off('booker-search', this.respondToBookerChange)
      document.removeEventListener('keydown', this.enter)
    }
  }
</script>

<style>
  @keyframes flasher {
    0% { color: #000 }
    50% { color: #999; }
    100% { color: #000; }
  }
  @keyframes flasher-bg {
    0% { background-color: #000 }
    50% { background-color: #214; }
    100% { background-color: #000; }
  }
  .content {
    background-color: #fff;
    margin: 15px 15px 0 15px;
    padding: 0px !important;
  }
  #advanced-weight-settings {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    transform: translateY(100%);
    transition: all 0.5s;
    z-index: 8887;
    background-color: rgba(0, 0, 0, 0.75);
    /* background: rgba(238, 238, 238, 0.95); */
  }
  #advanced-weight-settings.open {
    transform: translateY(0%);
  }
  #weight-buttons {
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;
    z-index: 8888;
  }
  #weight-settings-button-1 {
    display: inline-block;
    /* left: -141px; */
    margin-right: -3px;
    padding: 10px 12px;
    color: #eee;
    font-weight: bold;
    animation: flasher-bg;
    animation-duration: 2s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }
  #weight-settings-button-2 {
    /* left: -141px; */
    display: inline-block;
    background-color: #d2d6de;
    padding: 7px 14px;
    color: #444;
    font-weight: bold;
    animation: flasher;
    animation-duration: 2s;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
  }
  .form-steps-outer {
    background-image: url('/static/img/gold.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    background-color: #979292;
    background-blend-mode: multiply;
    width: 100%;
    overflow: hidden;
    transition: all 0.5s;
    padding: 6% 5% 6% 5%;
    color: #fff;
    position: relative;
  }
  .form-steps-outer.open {
    overflow-y: visible;
    min-height: 403px;
  }
  .advanced-settings-toggle h5 {
    text-align: center;
  }
  .advanced-settings-toggle i {
    transition: transform 0.5s;
  }
  .advanced-settings-toggle i.open:first-child {
    transform: rotate(180deg)
  }
  .advanced-settings-toggle i.open:nth-child(2) {
    transform: rotate(-180deg)
  }
  .advanced-settings-toggle {
    padding: 10px;
    cursor: pointer;
    border: 1px solid #f4f4f4
  }
  .advanced-settings {
    max-height: 0;
    transition: max-height 0.5s;
    overflow: hidden;
  }
  .advanced-settings.open {
    max-height: 5000px;
  }
  h3.box-title {
    padding-left: 15px;
    font-weight: bold;
    text-transform: uppercase;
  }
  @media (max-width: 992px) {
    .form-steps-outer.open {
      min-height: 550px;
    }
  }
  @media (max-width: 767px) {
    .form-steps-outer.open {
      height: 1095px;
    }
  }
  .backer {
    background: #fff;
    padding: 10px;
    color: #000;
    border-radius: 15px;
  }
  .backer input {
    background: none;
    font-weight: bold;
    /* padding: 20px 10px; */
    height: 44px;
    border: #c0c0c0 1px solid;
    border-right: none !important;
  }
  .backer i {
    color: #c0c0c0 !important;
  }
  .backer .input-group-addon {
    background-color: #fff !important;
    border: 1px solid #c0c0c0;
    border-left: none !important;
  }
  .backer button {
    background-color: #3c8dbc;
    color: #fff;
  }
  .backer button:hover, button:focus {
    color: #eee !important;
  }
  #rates-buttons {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 8888;
  }
  #advanced-settings-outer header {
    background: #3c8dbc;
    color: #fff;
    padding: 30px 50px;
  }
  .adv-tabs {
    display: flex;
    padding: 0 50px;
    color: #949292;
  }
  .tab {
    padding: 30px 0;
    margin-right: 25px;
    cursor: pointer;
  }
  .tab.disabled {
    cursor: not-allowed;
  }
  .tab.active {
    border-bottom: 2px solid #3c8dbc;
    color: #000;
    position: relative;
    top: 1px;
  }
  .adv-tabs-content {
    border: 1px solid #c0c0c0;
    background-color: #eee;
    padding: 30px;
  }
  .adv-tabs-content label {
    color: #949292;
  }
  .p-10 {
    padding: 10px;
  }
  .badge-container {
    margin-top: 10px;
  }
</style>



// WEBPACK FOOTER //
// QuoteForm.vue?5ed1aa77